/**
 * Kyle Rising — waveform display.
 *
 * <kr-waveform key="quarters"> draws that track's real amplitude peaks, read
 * from the table in kr-waveforms.js. Peaks are precomputed rather than decoded
 * in the browser: a song page can then show the waveform immediately, without
 * pulling down three megabytes of audio for a picture nobody asked to hear.
 *
 * A song added through the admin has no precomputed entry, so given a `src`
 * the element analyses the audio itself instead — only once it scrolls into
 * view, and only once per file however many waveforms show it. Same sampling
 * as tools/build-waveforms.mjs, so the two paths draw the same shape.
 *
 * Set `progress` (0-1) to fill the played portion. Interactive instances also
 * act as the scrubber and emit `kr-seek` with the ratio that was clicked; the
 * player keeps its range input alongside for keyboard and assistive tech, so
 * this stays decorative as far as accessibility is concerned.
 */
const PEAKS = 500;
// 3ms at each position: a couple of cycles of the low end. Matches the build.
const WINDOW_SECONDS = 0.003;

// One analysis per file, shared by every waveform showing it — the song page
// and the player bar both draw the same track.
const analysed = new Map();

function analyse(src) {
  if (analysed.has(src)) return analysed.get(src);

  const job = fetch(src)
    .then((res) => {
      if (!res.ok) throw new Error(`${res.status} fetching ${src}`);
      return res.arrayBuffer();
    })
    // Offline, so decoding needs no user gesture and never touches the speakers.
    .then((buffer) => new OfflineAudioContext(1, 1, 44100).decodeAudioData(buffer))
    .then((audio) => {
      const data = audio.getChannelData(0);
      const stride = data.length / PEAKS;
      const span = Math.max(Math.round(WINDOW_SECONDS * audio.sampleRate), 1);
      const peaks = [];
      let max = 0;

      for (let i = 0; i < PEAKS; i++) {
        const start = Math.floor(i * stride);
        let peak = 0;
        for (let j = 0; j < span && start + j < data.length; j++) {
          const v = Math.abs(data[start + j]);
          if (v > peak) peak = v;
        }
        peaks.push(peak);
        if (peak > max) max = peak;
      }

      return peaks.map((p) => Math.round((p / (max || 1)) * 100));
    });

  // A failed analysis is forgotten, so a later attempt can retry it.
  job.catch(() => analysed.delete(src));
  analysed.set(src, job);
  return job;
}

class KRWaveform extends HTMLElement {
  static observedAttributes = ['key', 'src'];

  connectedCallback() {
    if (!this.canvas) {
      this.canvas = document.createElement('canvas');
      this.canvas.setAttribute('aria-hidden', 'true');
      this.appendChild(this.canvas);
    }

    this.#read();

    // The element is sized by CSS, so redraws follow the box rather than the
    // window — the player bar changes width when text ellipsises, not only on
    // a window resize.
    this.observer = new ResizeObserver(() => this.#draw());
    this.observer.observe(this);

    if (this.hasAttribute('interactive')) {
      this.addEventListener('pointerdown', (event) => this.#seek(event));
    }

    this.#draw();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    this.visibility?.disconnect();
  }

  attributeChangedCallback() {
    if (!this.isConnected) return;
    this.#read();
    this.#draw();
  }

  set progress(value) {
    const next = Math.min(Math.max(Number(value) || 0, 0), 1);
    // Redrawing on every timeupdate is wasteful when nothing moved a pixel.
    if (Math.abs(next - (this.ratio || 0)) < 0.001) return;
    this.ratio = next;
    this.#draw();
  }

  get progress() {
    return this.ratio || 0;
  }

  #read() {
    const key = this.getAttribute('key');
    this.peaks = (key && window.KR_WAVEFORMS?.[key]) || null;
    this.toggleAttribute('data-empty', !this.peaks);

    if (!this.peaks) this.#analyseWhenVisible();
  }

  /**
   * Falls back to analysing the audio, deferred until the element is on
   * screen: a waveform nobody scrolls to shouldn't cost a 3MB download.
   */
  #analyseWhenVisible() {
    this.visibility?.disconnect();

    const src = this.getAttribute('src');
    if (!src) return;

    this.visibility = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      this.visibility.disconnect();

      analyse(src)
        .then((peaks) => {
          // The element may have moved on to another track while this ran.
          if (this.getAttribute('src') !== src) return;
          this.peaks = peaks;
          this.toggleAttribute('data-empty', false);
          this.#draw();
        })
        .catch(() => {
          /* no waveform, the rest of the page is unaffected */
        });
    });

    this.visibility.observe(this);
  }

  #seek(event) {
    if (!this.peaks) return;
    const box = this.getBoundingClientRect();
    if (!box.width) return;

    const ratio = Math.min(Math.max((event.clientX - box.left) / box.width, 0), 1);
    this.dispatchEvent(new CustomEvent('kr-seek', { bubbles: true, detail: { ratio } }));
  }

  /**
   * Mirrored spikes around the centre line. Peaks are downsampled by taking the
   * loudest value in each slice rather than averaging, because averaging is
   * exactly what turns a waveform back into a smooth blob.
   */
  #draw() {
    if (!this.canvas || !this.peaks) return;

    const width = this.clientWidth;
    const height = this.clientHeight;
    if (!width || !height) return;

    const dpr = window.devicePixelRatio || 1;
    this.canvas.width = Math.round(width * dpr);
    this.canvas.height = Math.round(height * dpr);

    const ctx = this.canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, width, height);

    const styles = getComputedStyle(this);
    const base = styles.getPropertyValue('--kr-wave-color').trim() || '#ffffff';
    const played = styles.getPropertyValue('--kr-wave-played').trim() || base;
    const step = Number(styles.getPropertyValue('--kr-wave-step')) || 3;
    const bar = Number(styles.getPropertyValue('--kr-wave-width')) || 2;

    const lines = Math.max(Math.floor(width / step), 1);
    const perLine = this.peaks.length / lines;
    const mid = height / 2;
    const cut = (this.ratio || 0) * width;

    for (let i = 0; i < lines; i++) {
      let peak = 0;
      const from = Math.floor(i * perLine);
      const to = Math.max(Math.floor((i + 1) * perLine), from + 1);
      for (let j = from; j < to && j < this.peaks.length; j++) {
        if (this.peaks[j] > peak) peak = this.peaks[j];
      }

      // Whole pixels, and filled rather than stroked: a stroked hairline lands
      // on a half pixel and comes out grey, which is what made this read as a
      // faint scribble rather than a waveform.
      const x = Math.round(i * step);
      // A floor of one pixel so silence still reads as a line, not a gap.
      const amp = Math.max((peak / 100) * mid, 0.5);

      ctx.fillStyle = x <= cut ? played : base;
      ctx.fillRect(x, mid - amp, bar, amp * 2);
    }
  }
}

if (!customElements.get('kr-waveform')) {
  customElements.define('kr-waveform', KRWaveform);
}
